import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import { useGetContractActivityQuery } from '@/apis/extended/activityApi';
import {
  useExtendContractDeadlineMutation,
  useOverrideContractClauseMutation,
  type ContractAdminView,
} from '@/apis/extended/admin/adminWorkspaceApi';
import type { ContractClauseComparison } from '@/components/Admin/ContractComparisonTable';

const fmtDate = (d?: string | null) => {
  if (!d) return 'not set';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : d;
};

/**
 * Written-acceptance deadline: a loud warning once it has passed (the offer
 * can't be signed until it moves) and a way to move it, with a reason.
 */
export function ContractDeadlineBanner({
  contractId,
  view,
}: {
  contractId: string;
  view?: ContractAdminView;
}) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [extend, { isLoading }] = useExtendContractDeadlineMutation();

  if (!view) return null;
  const passed = view.acceptanceDeadlinePassed;

  const save = async () => {
    try {
      await extend({ contractId, date, reason: reason.trim() }).unwrap();
      toast.success(`Deadline moved to ${fmtDate(date)}`);
      setOpen(false);
      setReason('');
    } catch (e: any) {
      toast.error(e?.data?.message || 'Could not change the deadline');
    }
  };

  return (
    <>
      <Alert
        severity={passed ? 'error' : 'info'}
        sx={{ mb: 3 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              setDate('');
              setOpen(true);
            }}
          >
            {passed ? 'Extend deadline' : 'Change'}
          </Button>
        }
      >
        {passed
          ? `The offer deadline (${fmtDate(
              passed
            )}) has passed. Nobody can sign until it moves.`
          : `Offer good until ${fmtDate(view.offerGoodUntil)}.`}
      </Alert>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Change the offer deadline</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Currently {fmtDate(view.offerGoodUntil)}. The change and your
              reason go in the contract history.
            </Typography>
            <TextField
              type="date"
              label="New deadline"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Reason (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={isLoading || !date || !reason.trim()}
            onClick={() => void save()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Reopen a clause or mark it agreed, with a required reason. */
export function ClauseDecisionDialog({
  contractId,
  clause,
  onClose,
}: {
  contractId: string;
  clause: ContractClauseComparison | null;
  onClose: () => void;
}) {
  const [action, setAction] = useState<'reopen' | 'agree'>('reopen');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [override, { isLoading }] = useOverrideContractClauseMutation();

  if (!clause) return null;
  const canOverride = Boolean(clause.clauseId);

  const save = async () => {
    try {
      await override({
        contractId,
        clauseId: clause.clauseId!,
        clauseName: clause.clauseName,
        action,
        value: action === 'agree' && value.trim() ? value.trim() : undefined,
        reason: reason.trim(),
      }).unwrap();
      toast.success(action === 'agree' ? 'Marked agreed' : 'Clause reopened');
      setReason('');
      setValue('');
      onClose();
    } catch (e: any) {
      toast.error(e?.data?.message || 'Could not save');
    }
  };

  const show = (v: unknown) => (v == null || v === '' ? '—' : String(v));

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{clause.clauseName}</DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            mb: 2,
          }}
        >
          {(
            [
              ['Seller', clause.sellerInput],
              ['Buyer', clause.buyerInput],
              ['Agent', clause.agentInput],
            ] as const
          ).map(([who, v]) => (
            <Box key={who}>
              <Typography variant="caption" color="text.secondary">
                {who}
              </Typography>
              <Typography fontWeight={600}>{show(v)}</Typography>
            </Box>
          ))}
        </Box>
        {clause.statusNote && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {clause.statusNote}
          </Alert>
        )}
        {!canOverride ? (
          <Alert severity="warning">
            This row comes from the buyer's profile, not a negotiated clause.
          </Alert>
        ) : (
          <Stack spacing={2}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={action}
              onChange={(_e, v) => v && setAction(v)}
            >
              <ToggleButton value="reopen">Reopen for negotiation</ToggleButton>
              <ToggleButton value="agree">Mark agreed</ToggleButton>
            </ToggleButtonGroup>
            {action === 'agree' && (
              <TextField
                label="Agreed value (optional)"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                size="small"
              />
            )}
            <TextField
              label="Reason (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              Saved to the contract history with your name.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {canOverride && (
          <Button
            variant="contained"
            disabled={isLoading || !reason.trim()}
            onClick={() => void save()}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** Everything that happened on the contract, newest first. */
export function ContractHistoryPanel({ contractId }: { contractId: string }) {
  const { data, isLoading, error } = useGetContractActivityQuery(contractId, {
    skip: !contractId,
  });
  const rows = data?.data ?? [];
  if (isLoading) return <LinearProgress />;
  if (error) return <Alert severity="error">Could not load the history.</Alert>;
  if (!rows.length) {
    return (
      <Typography color="text.secondary">No changes recorded yet.</Typography>
    );
  }
  return (
    <Stack divider={<Divider flexItem />} spacing={1.25}>
      {rows.map((r) => (
        <Box key={r._id} sx={{ display: 'flex', gap: 2 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 140 }}
          >
            {new Date(r.createdAt).toLocaleString()}
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2">
              {r.message || r.type.replace(/_/g, ' ')}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip
                size="small"
                variant="outlined"
                color={r.actorRole === 'admin' ? 'warning' : 'default'}
                label={
                  r.actorName ? `${r.actorName} · ${r.actorRole}` : r.actorRole
                }
              />
            </Stack>
          </Box>
        </Box>
      ))}
    </Stack>
  );
}
